import React, { useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  CircularProgress,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import translate from "../../../i18n/translate";
import ApiRequest from "../../../actions/ApiRequest";
import Toast from "../../common/Toast";

// Every EPRS product, grouped by biller. The switches and the titles are the
// admin's and survive a product sync; price, code and allowances come from
// EPRS and are shown read-only. `refreshKey` changes when a sync finishes,
// which reloads the table.
const EPRSProductsTable = ({ refreshKey, isDarktheme }) => {
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const [edits, setEdits] = useState({});
  const [busy, setBusy] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await ApiRequest.post("eprs_products?operation=get");
      if (res?.data?.status === "success") {
        setProducts(res.data.products || []);
        setEdits({});
      }
    } catch (err) {
      // the axios interceptor already reports the failure
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [refreshKey]);

  const groups = useMemo(() => {
    const byProvider = {};
    products.forEach((p) => {
      (byProvider[p.provider] = byProvider[p.provider] || []).push(p);
    });
    return Object.keys(byProvider)
      .sort((a, b) => byProvider[b].length - byProvider[a].length || a.localeCompare(b))
      .map((name) => ({ name, items: byProvider[name] }));
  }, [products]);

  const patch = (row, fields) =>
    setProducts((ps) => ps.map((p) => (p.row === row ? { ...p, ...fields } : p)));

  const edit = (row, field, value) =>
    setEdits((e) => ({ ...e, [row]: { ...(e[row] || {}), [field]: value } }));

  const pendingTitles = (p) => {
    const e = edits[p.row] || {};
    const body = {};
    ["en_title", "ar_title", "he_title"].forEach((f) => {
      if (e[f] !== undefined && e[f].trim() !== p[f]) body[f] = e[f].trim();
    });
    return body;
  };

  const saveProduct = async (p, extra = {}) => {
    const body = { row: p.row, ...pendingTitles(p), ...extra };
    if (Object.keys(body).length === 1) return;
    setBusy(p.row);
    try {
      const res = await ApiRequest.post("eprs_products?operation=update", body);
      if (res?.data?.status === "success") {
        patch(p.row, body);
        setEdits((e) => ({ ...e, [p.row]: {} }));
      } else {
        Toast.fire({ title: res?.data?.reason || "Something Went Wrong!", icon: "error" });
      }
    } catch (err) {
      Toast.fire({ title: "Something Went Wrong!", icon: "error" });
    } finally {
      setBusy(null);
    }
  };

  const switchBiller = async (name, active) => {
    setBusy(name);
    try {
      const res = await ApiRequest.post("eprs_products?operation=biller", { provider: name, active });
      if (res?.data?.status === "success") {
        setProducts((ps) => ps.map((p) => (p.provider === name ? { ...p, active } : p)));
      } else {
        Toast.fire({ title: res?.data?.reason || "Something Went Wrong!", icon: "error" });
      }
    } catch (err) {
      Toast.fire({ title: "Something Went Wrong!", icon: "error" });
    } finally {
      setBusy(null);
    }
  };

  const titleField = (p, field, label) => (
    <TextField
      size="small"
      fullWidth
      label={label}
      value={(edits[p.row] || {})[field] !== undefined ? edits[p.row][field] : p[field]}
      onChange={(ev) => edit(p.row, field, ev.target.value)}
      inputProps={{ style: { fontSize: 13 } }}
    />
  );

  if (loading && products.length === 0) {
    return (
      <Box sx={{ padding: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ marginTop: 3, color: isDarktheme ? "#fff" : "inherit" }}>
      <Typography variant="h6" sx={{ marginBottom: 0.5 }}>
        {translate("Products")} · {products.length}
      </Typography>
      <Typography variant="body2" sx={{ opacity: 0.75, marginBottom: 2, maxWidth: "70ch" }}>
        {translate("eprsProductsHint")}
      </Typography>

      {groups.map((g) => {
        const enabled = g.items.filter((p) => p.active).length;
        const allOn = enabled === g.items.length;
        return (
          <Accordion key={g.name} disableGutters>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2, width: "100%", paddingRight: 2 }}>
                <Switch
                  size="small"
                  checked={enabled > 0}
                  disabled={busy === g.name}
                  onClick={(ev) => ev.stopPropagation()}
                  onChange={(ev) => switchBiller(g.name, ev.target.checked)}
                />
                <Typography sx={{ fontWeight: 600, flex: 1 }}>{g.name}</Typography>
                <Chip
                  size="small"
                  color={enabled === 0 ? "default" : allOn ? "success" : "warning"}
                  label={<>{enabled} / {g.items.length} {translate("enabled")}</>}
                />
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ padding: 0, overflowX: "auto" }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell />
                    <TableCell>{translate("Titles")}</TableCell>
                    <TableCell align="right">₪</TableCell>
                    <TableCell>{translate("Allowances")}</TableCell>
                    <TableCell>{translate("Code")}</TableCell>
                    <TableCell align="right" />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {g.items.map((p) => {
                    const dirty = Object.keys(pendingTitles(p)).length > 0;
                    return (
                      <TableRow key={p.row} hover sx={{ opacity: p.active ? 1 : 0.55 }}>
                        <TableCell sx={{ width: 60 }}>
                          <Switch
                            size="small"
                            checked={p.active}
                            disabled={busy === p.row}
                            onChange={(ev) => saveProduct(p, { active: ev.target.checked })}
                          />
                        </TableCell>
                        <TableCell sx={{ minWidth: 320 }}>
                          <Box sx={{ display: "grid", gap: 1 }}>
                            {titleField(p, "en_title", "EN")}
                            {titleField(p, "ar_title", "AR")}
                            {titleField(p, "he_title", "HE")}
                          </Box>
                          {p.en_des && (
                            <Typography variant="caption" sx={{ opacity: 0.6 }}>
                              {p.en_des}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="right" sx={{ whiteSpace: "nowrap", fontWeight: 600 }}>
                          {p.amount}
                        </TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap", fontSize: 12 }}>
                          {[
                            p.min && p.min !== "0" ? `${p.min} min` : null,
                            p.gb && p.gb !== "0" ? `${p.gb} GB` : null,
                            p.sms && p.sms !== "0" ? `${p.sms} SMS` : null,
                            p.valid && p.valid !== "0" ? p.valid : null,
                          ]
                            .filter(Boolean)
                            .join(" · ") || "—"}
                        </TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap", fontSize: 12 }}>
                          {p.code}
                          <Typography variant="caption" sx={{ display: "block", opacity: 0.6 }}>
                            EPRS {p.eprs_id}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Button
                            size="small"
                            variant="contained"
                            disabled={!dirty || busy === p.row}
                            onClick={() => saveProduct(p)}
                          >
                            {busy === p.row ? <CircularProgress size={16} color="inherit" /> : translate("Save")}
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Box>
  );
};

const mapStateToProps = ({ darkTheme: { isDarktheme } }) => ({ isDarktheme });

export default connect(mapStateToProps)(EPRSProductsTable);
